"use client"

import { useState, useEffect, useCallback, CSSProperties } from "react"
import { cn } from "@/lib/utils"

// Extend CSSProperties to support CSS custom properties
interface ConfettiStyle extends CSSProperties {
  "--delay"?: string
  "--duration"?: string
}

interface ConfettiPiece {
  id: number
  x: number
  delay: number
  duration: number
  color: string
  rotation: number
  size: number
  shape: "square" | "rectangle" | "circle"
}

interface ConfettiProps {
  trigger: boolean
  duration?: number
  pieceCount?: number
  colors?: string[]
  className?: string
  onComplete?: () => void
}

/**
 * CSS-only confetti celebration effect
 *
 * No external libraries - uses CSS animations for performance.
 * Spawns confetti pieces from the top of the screen that fall
 * with random rotation, colors, and sizes.
 *
 * Automatically respects prefers-reduced-motion.
 */
export function Confetti({
  trigger,
  duration = 3000,
  pieceCount = 50,
  colors = ["#22c55e", "#10b981", "#059669", "#047857", "#fbbf24", "#f59e0b"],
  className,
  onComplete
}: ConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([])
  const [isActive, setIsActive] = useState(false)

  const generatePieces = useCallback(() => {
    const shapes: ConfettiPiece["shape"][] = ["square", "rectangle", "circle"]

    return Array.from({ length: pieceCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100, // % from left
      delay: Math.random() * 0.8, // stagger start (0-0.8s)
      duration: 2 + Math.random() * 2, // 2-4s fall time
      color: colors[Math.floor(Math.random() * colors.length)],
      rotation: Math.random() * 360,
      size: 6 + Math.random() * 10, // 6-16px
      shape: shapes[Math.floor(Math.random() * shapes.length)],
    }))
  }, [pieceCount, colors])

  useEffect(() => {
    if (trigger && !isActive) {
      // Check for reduced motion preference
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches

      if (prefersReducedMotion) {
        // Skip animation, just call onComplete
        onComplete?.()
        return
      }

      setIsActive(true)
      setPieces(generatePieces())

      // Clear after animation completes
      const timer = setTimeout(() => {
        setPieces([])
        setIsActive(false)
        onComplete?.()
      }, duration + 1000) // Extra time for last pieces to finish

      return () => clearTimeout(timer)
    }
  }, [trigger, isActive, duration, generatePieces, onComplete])

  if (pieces.length === 0) return null

  return (
    <div
      className={cn(
        "fixed inset-0 pointer-events-none overflow-hidden z-50",
        className
      )}
      aria-hidden="true"
    >
      {pieces.map((piece) => {
        const shapeStyles = {
          square: { borderRadius: "2px" },
          rectangle: {
            borderRadius: "1px",
            width: piece.size * 0.6,
            height: piece.size * 1.4,
          },
          circle: { borderRadius: "50%" },
        }

        return (
          <div
            key={piece.id}
            className="absolute animate-confetti-fall"
            style={{
              "--delay": `${piece.delay}s`,
              "--duration": `${piece.duration}s`,
              left: `${piece.x}%`,
              top: "-20px",
              width: piece.size,
              height: piece.size,
              backgroundColor: piece.color,
              transform: `rotate(${piece.rotation}deg)`,
              ...shapeStyles[piece.shape],
            } as ConfettiStyle}
          />
        )
      })}
    </div>
  )
}

/**
 * Hook for easily triggering confetti
 */
export function useConfetti() {
  const [showConfetti, setShowConfetti] = useState(false)

  const triggerConfetti = useCallback(() => {
    setShowConfetti(true)
  }, [])

  const resetConfetti = useCallback(() => {
    setShowConfetti(false)
  }, [])

  return {
    showConfetti,
    triggerConfetti,
    resetConfetti,
    ConfettiComponent: (props: Omit<ConfettiProps, "trigger">) => (
      <Confetti
        {...props}
        trigger={showConfetti}
        onComplete={() => {
          resetConfetti()
          props.onComplete?.()
        }}
      />
    ),
  }
}

/**
 * Green-themed confetti preset for lawn care celebrations
 */
export function LawnConfetti({
  trigger,
  onComplete
}: {
  trigger: boolean
  onComplete?: () => void
}) {
  return (
    <Confetti
      trigger={trigger}
      colors={[
        "#22c55e", // green-500
        "#16a34a", // green-600
        "#15803d", // green-700
        "#10b981", // emerald-500
        "#059669", // emerald-600
        "#047857", // emerald-700
        "#fbbf24", // amber-400 (accent)
      ]}
      pieceCount={60}
      duration={3500}
      onComplete={onComplete}
    />
  )
}
